import { DISTRIBUTION_TYPES } from '../../framework/constants/spend.constants';
import { spendActionTypes } from './spend.actions';

// todo: maube different types based on GC / manager
export interface ILineItem {
  id: number;
  value: number | null;
  name: string;
  budget: IBudget[];
  start_date: string;
  duration: number;
  distribution: DISTRIBUTION_TYPES;
  prime_start_date: string;
  prime_duration: number;
  prime_distribution: DISTRIBUTION_TYPES;
  commitment_start_date: string;
  project_id?: number;
  row_number: number;
  committed_items: ICommittedItem[];
  used_in_pc: boolean;
  division?: string;
  cost_type?: string;
  account_code?: string;
  original_budget_total?: number; // added on frontend
  subitems: ISubitem[]; // valid for GC only
}

export interface ISubitem extends ILineItem {
  parent_id?: number; // added on frontend
  is_subitem?: boolean; // added on frontend
}

export interface ILineItemPayload {
  id?: number;
  name: string;
  budget: IBudget[];
  start_date: string;
  prime_start_date?: string;
  duration: number;
  prime_duration?: number;
  distribution: DISTRIBUTION_TYPES;
  prime_distribution?: DISTRIBUTION_TYPES;
  row_number: number;
  project_id: number;
}

export interface ICommittedItem {
  id: number;
  commitment_id: number;
  row_number: number;
  committed_row_number: number; // used only when view is budget + commitments
  budget: IForecastBudget[];
  start_date: string;
  duration: number;
  distribution: DISTRIBUTION_TYPES;
  prime_start_date: string;
  prime_duration: number;
  prime_distribution: DISTRIBUTION_TYPES;
  project_id?: number;
  commitment_name: string;
  commitment_title: string;
  item_id: number;
  original_budget_total?: number; // added on frontend
  type: 'commitment' | 'forecast_modification';
}

export interface ICommittedItemPayload {
  id: number;
  row_number: number;
  committed_row_number: number; // used only when view is budget + commitments
  budget?: IForecastBudget[];
  distribution: DISTRIBUTION_TYPES;
}

export interface ILineItemExtended extends ILineItem {
  // the budget is filtered by type and year
  monthly_data: IMonthlySpendData;
  monthly_disable: IMonthlyDisable;
  prime_monthly_disable: IMonthlyDisable;
  year_total: number;
  project_total: number;
  original_budget_total?: number;
  original_prime_total?: number;
  committed_items: ICommittedItemExtended[];
  subitems: ILineItemExtended[]; // valid for GC only
}

export interface ISubitemExtended extends ILineItemExtended {
  parent_id: number;
  is_subitem?: boolean; // added on frontend
}

export interface ICommittedItemExtended extends ICommittedItem {
  monthly_data: IMonthlySpendData;
  monthly_disable: IMonthlyDisable;
  year_total: number;
  project_total: number;
  lineItem?: ILineItemExtended;
}

export interface ISpreadLineItem extends ILineItem {
  yearTotalFormula: string;
  budgetTotalFormula: string;
  committed_items: ISpreadCommittedItem[];
}

export interface ISpreadCommittedItem extends ICommittedItem {
  yearTotalFormula: string;
  dollarsTotalFormula: string;
}

export interface ILineItemsTotal {
  // the budget is filtered by type and year
  monthly_data: IMonthlySpendData;
  year_total: number;
  project_total: number;
}

export interface IMonthlySpendData {
  1: number;
  2: number;
  3: number;
  4: number;
  5: number;
  6: number;
  7: number;
  8: number;
  9: number;
  10: number;
  11: number;
  12: number;
}

export interface IMonthlyDisable {
  1: boolean;
  2: boolean;
  3: boolean;
  4: boolean;
  5: boolean;
  6: boolean;
  7: boolean;
  8: boolean;
  9: boolean;
  10: boolean;
  11: boolean;
  12: boolean;
}

export interface IForecastBudget {
  year: number;
  monthly_forecast: IMonthlySpendData;
}

export interface IBudget {
  year: number;
  monthly_budget: IMonthlySpendData;
  monthly_prime?: IMonthlySpendData;
}

export interface IBudgetPartial {
  year: number;
  monthly_budget?: IMonthlySpendData;
  monthly_forecast?: IMonthlySpendData;
}

export type DistributionFieldType =
  | 'monthly_budget'
  | 'monthly_forecast'
  | 'monthly_prime'
  | 'monhtly_prime_actuals';

export interface ISpendDistribution {
  budget: number;
  start_date: string;
  duration: number;
  distribution: DISTRIBUTION_TYPES;
  field: DistributionFieldType;
}

export interface IPrimeSpendDistribution {
  budget: number;
  prime_start_date: string;
  prime_duration: number;
  prime_distribution: DISTRIBUTION_TYPES;
  field: DistributionFieldType;
}

export interface SpendDistributionRequest
  extends Partial<ISpendDistribution>,
    Partial<IPrimeSpendDistribution> {
  item_id?: number;
}

export enum SpendStoreUpdateTypes {
  NONE,
  SET_ALL,
  ADD,
  UPDATE,
  UPDATE_SUBITEM,
  DELETE,
  FILTER_CHANGE, // spend type or selected year change
  SAVE_BACKEND,
}

export interface ISpendStoreUpdate {
  type: SpendStoreUpdateTypes;
  lineId: number;
  subitemId?: number;
}

export interface ILastSpendStoreUpdate {
  lastStoreUpdate: ISpendStoreUpdate;
  lineItems: ILineItemExtended[];
}

export type SpreadSheetInputData = {
  items: ILineItem[];
  isEditable: Record<number, boolean[]>[];
  selectedYear: number;
};

export interface ISpreadSheetChange {
  newValue: number;
  lineIndex: number; // indexed from 0, like arrays
  monthIndex: number; // indexed from 1, like months here
  sheetYear: number;
}

export interface IDistributionResponse {
  budget: IForecastBudget[] | IBudget[];
}

export enum SPEND_ERROR_TYPES {
  NEW,
  MODIFIED,
  DELETED,
  FAILED_TO_UPDATE_COMMITTED_LINE,
  FAILED_TO_UPDATE_SUBITEM,
}

export type SpendError = {
  id;
  errorType: SPEND_ERROR_TYPES;
  error: true;
};

export interface ISpendLineItemSummary {
  name: string;
  spend_contracts: SpendContracts;
  spend_direct_costs: SpendDirectCosts;
  committed: number;
  isLoaded?: boolean;
}

export interface SpendContracts {
  contracts: Contract[];
  committed: number;
}

export interface Contract {
  title: string;
  company_name: string;
  committed: number;
  commitments: Commitment[];
}

export interface Commitment {
  title: string;
  committed: number;
  type: string;
}

export interface SpendDirectCosts {
  direct_costs: DirectCost[];
  committed: number;
}

export interface DirectCost {
  title: string;
  committed: number;
}

export type UpdateDistributionAction = ReturnType<
  | typeof spendActionTypes.updateDistribution
  | typeof spendActionTypes.updateForecastDistribution
  | typeof spendActionTypes.updateSubitemDistribution
  | typeof spendActionTypes.updatePrimeSubitemDistribution
>;

export type SpendExportRow = {
  id: number;
  division: string;
  cost_type: string;
  description: string;
  dollar_amount: number;
  parent_id: number;
};

export type ILineItemTemporary = ILineItem & { id: string | null };

export type ILineItemSimple = {
  id: number;
  name: string;
  division: string;
  cost_type: string;
};
